import Dexie, { liveQuery, type ObservabilitySet } from 'dexie';
import { LiveValue, type Live } from '@/model';
import type { AccountStore } from './store';
import { i18next } from '@/i18n';

interface QueryEntry { live: Live<unknown>; start(store?: AccountStore): void; reset(): void; refresh(): void; invalidateIdle(): void }
/** Memoized Lives are retained across sign-in/out; changing accounts first publishes empty snapshots. */
export class Queries {
  constructor() { i18next.on('languageChanged', this.namesChanged); }
  private readonly namesChanged = () => {
    for (const [key, entry] of this.entries) if (key.startsWith('summary:') || key.startsWith('conversations:') || key === 'readable-conversations' || key === 'message-activity') entry.refresh();
  };
  private store?: AccountStore;
  private stopMutations = () => undefined as void;
  private readonly entries = new Map<string, QueryEntry>();
  get<T>(key: string, initial: T, read: (store: AccountStore) => Promise<T>): Live<T> {
    const existing = this.entries.get(key);
    if (existing) return existing.live as Live<T>;
    const value = new LiveValue(initial); let sequence = 0; let stop = () => undefined as void;
    let reading: Promise<void> | undefined;
    const publish = (next: T, current: number) => { if (sequence === current && JSON.stringify(next) !== JSON.stringify(value.get())) value.set(next); };
    const pause = () => { sequence++; stop(); stop = () => undefined; reading = undefined; };
    const clear = () => { pause(); value.set(initial); };
    const readOnce = () => {
      const store = this.store; if (!store || reading) return;
      const current = sequence;
      const task = Promise.resolve().then(async () => { if (current === sequence) publish(await read(store), current); }).catch(() => { /* Keep the last readable projection. */ }).finally(() => { if (reading === task) reading = undefined; });
      reading = task;
    };
    const live: Live<T> = {
      get: () => { if (!value.listenerCount) readOnce(); return value.get(); },
      subscribe: listener => {
        const off = value.subscribe(() => listener());
        if (value.listenerCount === 1) entry.start(this.store);
        let active = true;
        return () => { if (!active) return; active = false; off(); if (!value.listenerCount) clear(); };
      },
    };
    const entry = {
      live,
      reset: () => { clear(); entry.start(this.store); },
      refresh: () => { if (value.listenerCount) entry.start(this.store); else { clear(); readOnce(); } },
      // Passive one-shot readers also lose their snapshots and pending results on a commit.
      invalidateIdle: () => { if (!value.listenerCount) clear(); },
      start: (store?: AccountStore) => {
        pause(); const current = sequence;
        if (!store) { value.set(initial); return; }
        if (!value.listenerCount) return;
        const subscription = liveQuery(() => read(store)).subscribe({
          next: value => publish(value, current),
          error: () => { /* Preserve the last readable projection on a storage failure. */ },
        });
        stop = () => subscription.unsubscribe();
      },
    };
    this.entries.set(key, entry); return live;
  }
  setStore(store?: AccountStore): void {
    this.stopMutations(); this.stopMutations = () => undefined; this.store = store;
    if (store) {
      // This notification does no database reads and covers commits from other Dexie handles/tabs.
      // Never retain an idle private projection across a write while its liveQuery is stopped.
      const prefix = `idb://${store.name}/`;
      const changed = (parts: ObservabilitySet) => {
        if (Object.keys(parts).some(part => part.startsWith(prefix))) for (const entry of this.entries.values()) entry.invalidateIdle();
      };
      Dexie.on.storagemutated.subscribe(changed);
      this.stopMutations = () => Dexie.on.storagemutated.unsubscribe(changed);
    }
    for (const entry of this.entries.values()) entry.reset();
  }
  refresh(key: string): void { this.entries.get(key)?.refresh(); }
  dispose(): void { i18next.off('languageChanged', this.namesChanged); this.setStore(); }
}
